const slugFromText = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || 'item';

const safeHref = (href = '') => {
  const value = String(href).trim();
  if (!value) return '#';
  if (/^(https?:|mailto:|tel:)/i.test(value)) return value;
  if (value.startsWith('/') || value.startsWith('#')) return value;
  return '#';
};

const stripTableCell = (cell = '') => cell.trim().replace(/^\\\|/, '|').replace(/\\\|$/, '|');

const isDividerRow = (line = '') => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const splitTableRow = (line = '') => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map(stripTableCell);
};

const pushParagraph = (blocks, lines) => {
  if (!lines.length) return;
  blocks.push({ type: 'paragraph', text: lines.join(' ') });
  lines.length = 0;
};

const parseMarkdown = (text) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  const paragraph = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      pushParagraph(blocks, paragraph);
      i += 1;
      continue;
    }

    const fence = trimmed.match(/^```(.*)$/);
    if (fence) {
      pushParagraph(blocks, paragraph);
      const code = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        code.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'code', language: fence[1].trim(), text: code.join('\n') });
      i += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      pushParagraph(blocks, paragraph);
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (/^---+$/.test(trimmed)) {
      pushParagraph(blocks, paragraph);
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('>')) {
      pushParagraph(blocks, paragraph);
      const quote = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quote.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', text: quote.join('\n') });
      continue;
    }

    if (trimmed.includes('|') && i + 1 < lines.length && isDividerRow(lines[i + 1])) {
      pushParagraph(blocks, paragraph);
      const headers = splitTableRow(trimmed);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().includes('|') && lines[i].trim()) {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    const unordered = trimmed.match(/^[-*+]\s+(.+)$/);
    const ordered = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (unordered || ordered) {
      pushParagraph(blocks, paragraph);
      const orderedList = !!ordered;
      const items = [];
      while (i < lines.length) {
        const current = lines[i].trim();
        const match = orderedList ? current.match(/^\d+[.)]\s+(.+)$/) : current.match(/^[-*+]\s+(.+)$/);
        if (!match) break;
        items.push(match[1]);
        i += 1;
      }
      blocks.push({ type: orderedList ? 'ol' : 'ul', items });
      continue;
    }

    paragraph.push(trimmed);
    i += 1;
  }
  pushParagraph(blocks, paragraph);
  return blocks;
};

const renderInline = (text, testId, inlineScope = 'inline') => {
  const nodes = [];
  const pattern = /(\[[^\]]+\]\([^)]+\)|`[^`]+`|~~[^~]+~~|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const href = safeHref(linkMatch[2]);
        nodes.push(<a key={`${match.index}-a`} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noreferrer' : undefined} data-testid={`${testId}-${inlineScope}-link-${slugFromText(`${linkMatch[1]}-${href}`)}`}>{linkMatch[1]}</a>);
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith('`')) {
      nodes.push(<code key={`${match.index}-code`} data-testid={`${testId}-${inlineScope}-inline-code-${match.index}`}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('~~')) {
      nodes.push(<del key={`${match.index}-del`}>{token.slice(2, -2)}</del>);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(<strong key={`${match.index}-strong`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*') || token.startsWith('_')) {
      nodes.push(<em key={`${match.index}-em`}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(token);
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const renderListItem = (item, key, testId, index) => {
  const task = item.match(/^\[(x|X| )\]\s+(.+)$/);
  if (!task) return <li key={key}>{renderInline(item, testId, `list-item-${index}`)}</li>;
  return (
    <li key={key} className="wc-markdown-task-item">
      <input type="checkbox" checked={task[1].toLowerCase() === 'x'} readOnly aria-label="Markdown task item" data-testid={`${testId}-task-checkbox-${index}`} />
      <span>{renderInline(task[2], testId, `task-item-${index}`)}</span>
    </li>
  );
};

const renderBlock = (block, index, testId) => {
  const key = `${block.type}-${index}`;
  if (block.type === 'heading') {
    const HeadingTag = `h${block.level}`;
    return <HeadingTag key={key} data-testid={`${testId}-heading-${block.level}-${index}`}>{renderInline(block.text, testId, `heading-${index}`)}</HeadingTag>;
  }
  if (block.type === 'paragraph') return <p key={key} data-testid={`${testId}-paragraph-${index}`}>{renderInline(block.text, testId, `paragraph-${index}`)}</p>;
  if (block.type === 'ul') return <ul key={key} data-testid={`${testId}-unordered-list-${index}`}>{block.items.map((item, itemIndex) => renderListItem(item, `${key}-${itemIndex}`, testId, `${index}-${itemIndex}`))}</ul>;
  if (block.type === 'ol') return <ol key={key} data-testid={`${testId}-ordered-list-${index}`}>{block.items.map((item, itemIndex) => renderListItem(item, `${key}-${itemIndex}`, testId, `${index}-${itemIndex}`))}</ol>;
  if (block.type === 'quote') return <blockquote key={key} data-testid={`${testId}-quote-${index}`}>{block.text.split('\n').map((line, lineIndex) => <p key={`${key}-${lineIndex}`}>{renderInline(line, testId, `quote-${index}-${lineIndex}`)}</p>)}</blockquote>;
  if (block.type === 'hr') return <hr key={key} data-testid={`${testId}-divider-${index}`} />;
  if (block.type === 'code') return <pre key={key} data-testid={`${testId}-code-block-${index}`}><code data-testid={`${testId}-block-code-${index}`}>{block.text}</code></pre>;
  if (block.type === 'table') {
    return (
      <div key={key} className="wc-markdown-table-wrap" data-testid={`${testId}-table-wrap-${index}`}>
        <table data-testid={`${testId}-table-${index}`}>
          <thead><tr>{block.headers.map((header, headerIndex) => <th key={`${key}-h-${headerIndex}`}>{renderInline(header, testId, `table-${index}-head-${headerIndex}`)}</th>)}</tr></thead>
          <tbody>{block.rows.map((row, rowIndex) => <tr key={`${key}-r-${rowIndex}`}>{block.headers.map((_, cellIndex) => <td key={`${key}-c-${rowIndex}-${cellIndex}`}>{renderInline(row[cellIndex] || '', testId, `table-${index}-cell-${rowIndex}-${cellIndex}`)}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  return null;
};

export default function MarkdownRenderer({ content, variant = 'default', testId = 'markdown-renderer' }) {
  const text = typeof content === 'string' ? content.trim() : '';
  if (!text) return null;

  const compact = variant === 'compact';
  const alert = variant === 'alert';
  const blocks = parseMarkdown(text);

  return (
    <div
      className={`${compact ? 'wc-markdown wc-markdown-compact' : 'wc-markdown'} ${alert ? 'wc-markdown-alert' : ''}`}
      data-testid={testId}
    >
      {blocks.map((block, index) => renderBlock(block, index, testId))}
    </div>
  );
}
